import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import adminCatalogApi from '../api/adminCatalogApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const emptyAppProvider = { company: '', appName: '', extra: '' };
const emptyLmsType = { name: '', physical: false, lmsValue: null };
const messageFor = (error, fallback) => {
  const data = error && error.response && error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') return data.detail || data.title || data.message || fallback;
  return (error && error.message) || fallback;
};

const AdminCatalog = () => {
  const msgs = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({ deniedRedirectTo: '/customers' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appProviders, setAppProviders] = useState([]);
  const [lmsTypes, setLmsTypes] = useState([]);
  const [dialogType, setDialogType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [appProviderForm, setAppProviderForm] = useState(emptyAppProvider);
  const [lmsTypeForm, setLmsTypeForm] = useState(emptyLmsType);

  const showError = (error, fallback) => {
    if (!handleAccessError(error) && msgs.current) msgs.current.show({ severity: 'error', summary: '', detail: messageFor(error, fallback), sticky: true });
  };
  const loadCatalog = async () => {
    try {
      clearAccessIssue();
      setLoading(true);
      const result = await Promise.all([adminCatalogApi.getAppProviders(), adminCatalogApi.getLmsTypes()]);
      setAppProviders(result[0]);
      setLmsTypes(result[1]);
    } catch (error) {
      showError(error, 'Unable to load catalog settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCatalog(); }, []);

  const openCreate = (type) => {
    setDialogType(type);
    setEditingId(null);
    if (type === 'appProvider') setAppProviderForm(emptyAppProvider);
    else setLmsTypeForm(emptyLmsType);
  };
  const openEdit = async (type, id) => {
    try {
      setSaving(true);
      const item = type === 'appProvider' ? await adminCatalogApi.getAppProvider(id) : await adminCatalogApi.getLmsType(id);
      setDialogType(type);
      setEditingId(id);
      if (type === 'appProvider') setAppProviderForm({ company: item.company || '', appName: item.appName || '', extra: item.extra || '' });
      else setLmsTypeForm({ name: item.name || '', physical: Boolean(item.physical), lmsValue: Number(item.lmsValue) || null });
    } catch (error) {
      showError(error, 'Unable to load catalog item.');
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    const isAppProvider = dialogType === 'appProvider';
    const form = isAppProvider ? appProviderForm : lmsTypeForm;
    if (isAppProvider && (!form.company.trim() || form.company.trim().length > 50 || !form.appName.trim() || form.appName.trim().length > 50)) {
      if (msgs.current) msgs.current.show({ severity: 'warn', summary: '', detail: 'Company and app name are required and must be 50 characters or fewer.' });
      return;
    }
    if (!isAppProvider && (!form.name.trim() || form.name.trim().length > 50 || !Number.isFinite(Number(form.lmsValue)) || Number(form.lmsValue) <= 0)) {
      if (msgs.current) msgs.current.show({ severity: 'warn', summary: '', detail: 'LMS name is required (50 characters or fewer) and LMS value must be greater than zero.' });
      return;
    }
    const request = isAppProvider ? { company: form.company.trim(), appName: form.appName.trim(), extra: String(form.extra || '').trim() } : { name: form.name.trim(), physical: Boolean(form.physical), lmsValue: Number(form.lmsValue) };
    try {
      setSaving(true);
      if (isAppProvider) {
        if (editingId === null) await adminCatalogApi.createAppProvider(request);
        else await adminCatalogApi.updateAppProvider(editingId, request);
      } else if (editingId === null) await adminCatalogApi.createLmsType(request);
      else await adminCatalogApi.updateLmsType(editingId, request);
      setDialogType(null);
      if (msgs.current) msgs.current.show({ severity: 'success', summary: '', detail: `${isAppProvider ? 'App Provider' : 'LMS Type'} saved.` });
      await loadCatalog();
      window.dispatchEvent(new Event('smarttile:catalog-updated'));
    } catch (error) {
      showError(error, `Unable to save ${isAppProvider ? 'App Provider' : 'LMS Type'}.`);
    } finally {
      setSaving(false);
    }
  };

  if (accessIssue) return <AccessState {...accessIssue} />;
  const isAppProvider = dialogType === 'appProvider';
  const dialogFooter = <><Button label="Cancel" className="p-button-text" disabled={saving} onClick={() => setDialogType(null)} /><Button label="Save" icon="pi pi-save" loading={saving} disabled={saving} onClick={save} /></>;
  return <Card className="justify-content-md-center mt-5"><Container fluid="md"><h4 className="mb-3">Admin Catalog</h4><Messages ref={msgs} />{loading ? <ProgressBar mode="indeterminate" style={{ height: '6px' }} /> : <><div className="flex align-items-center justify-content-between mb-2"><h5 className="mb-0">App Providers</h5><Button label="Add App Provider" icon="pi pi-plus" onClick={() => openCreate('appProvider')} /></div><DataTable value={appProviders} emptyMessage="No App Providers found." responsiveLayout="scroll"><Column field="appName" header="App name" /><Column field="company" header="Company" /><Column field="extra" header="Extra" /><Column header="" body={(row) => <Button icon="pi pi-pencil" className="p-button-text" aria-label="Edit App Provider" onClick={() => openEdit('appProvider', row.id)} />} /></DataTable><div className="flex align-items-center justify-content-between mt-5 mb-2"><h5 className="mb-0">LMS Types</h5><Button label="Add LMS Type" icon="pi pi-plus" onClick={() => openCreate('lmsType')} /></div><DataTable value={lmsTypes} emptyMessage="No LMS Types found." responsiveLayout="scroll"><Column field="name" header="Name" /><Column header="Physical" body={(row) => row.physical ? 'Yes' : 'No'} /><Column field="lmsValue" header="LMS value" /><Column header="" body={(row) => <Button icon="pi pi-pencil" className="p-button-text" aria-label="Edit LMS Type" onClick={() => openEdit('lmsType', row.id)} />} /></DataTable></>}<Dialog header={`${editingId === null ? 'Add' : 'Edit'} ${isAppProvider ? 'App Provider' : 'LMS Type'}`} visible={Boolean(dialogType)} onHide={() => !saving && setDialogType(null)} footer={dialogFooter} style={{ width: '32rem' }} breakpoints={{ '641px': '100vw' }}>{isAppProvider ? <div className="p-fluid"><div className="field mb-3"><label htmlFor="catalog-company">Company</label><InputText id="catalog-company" value={appProviderForm.company} maxLength={50} onChange={(e) => setAppProviderForm({ ...appProviderForm, company: e.target.value })} /></div><div className="field mb-3"><label htmlFor="catalog-app-name">App name</label><InputText id="catalog-app-name" value={appProviderForm.appName} maxLength={50} onChange={(e) => setAppProviderForm({ ...appProviderForm, appName: e.target.value })} /></div><div className="field"><label htmlFor="catalog-extra">Extra</label><InputText id="catalog-extra" value={appProviderForm.extra} onChange={(e) => setAppProviderForm({ ...appProviderForm, extra: e.target.value })} /></div></div> : <div className="p-fluid"><div className="field mb-3"><label htmlFor="catalog-lms-name">Name</label><InputText id="catalog-lms-name" value={lmsTypeForm.name} maxLength={50} onChange={(e) => setLmsTypeForm({ ...lmsTypeForm, name: e.target.value })} /></div><div className="field mb-3"><label htmlFor="catalog-lms-value">LMS value</label><InputNumber id="catalog-lms-value" value={lmsTypeForm.lmsValue} min={1} useGrouping={false} onValueChange={(e) => setLmsTypeForm({ ...lmsTypeForm, lmsValue: e.value })} /></div><div className="field"><label htmlFor="catalog-physical" className="d-block mb-2">Physical</label><InputSwitch id="catalog-physical" checked={lmsTypeForm.physical} onChange={(e) => setLmsTypeForm({ ...lmsTypeForm, physical: e.value })} /></div></div>}</Dialog></Container></Card>;
};

export default AdminCatalog;